import { EMPTY_INTENT, STRIKES, aiIntent, type Combatant, type Intent } from './simulation'

export type FightExperienceMode='versus'|'training'|'rift-ladder'|'survival-endurance'
export type CpuProfile='standard'|'training-dummy'|'aggressive'

export type FightExperienceRules={
  id:FightExperienceMode
  label:string
  description:string
  roundsToWin:number
  timerSeconds:number|null
  cpuProfile:CpuProfile
  autoResetOnKo:boolean
  ladder:boolean
  carryHealthBetweenOpponents:boolean
  recoveryBetweenOpponents:number
  rotateArenaBetweenOpponents:boolean
}

export const FIGHT_EXPERIENCE_RULES:Record<FightExperienceMode,FightExperienceRules>={
  versus:{
    id:'versus',label:'VERSUS',description:'Classic first-to-two fight with CPU or local rival.',roundsToWin:2,timerSeconds:99,cpuProfile:'standard',autoResetOnKo:false,ladder:false,carryHealthBetweenOpponents:false,recoveryBetweenOpponents:1,rotateArenaBetweenOpponents:false,
  },
  training:{
    id:'training',label:'TRAINING LAB',description:'Infinite practice loop with a reactive dummy, no timer loss and instant KO reset.',roundsToWin:1,timerSeconds:null,cpuProfile:'training-dummy',autoResetOnKo:true,ladder:false,carryHealthBetweenOpponents:false,recoveryBetweenOpponents:1,rotateArenaBetweenOpponents:false,
  },
  'rift-ladder':{
    id:'rift-ladder',label:'RIFT LADDER',description:'Deterministic roster gauntlet. One round per rival and arena rotation after victory.',roundsToWin:1,timerSeconds:99,cpuProfile:'standard',autoResetOnKo:false,ladder:true,carryHealthBetweenOpponents:false,recoveryBetweenOpponents:1,rotateArenaBetweenOpponents:true,
  },
  'survival-endurance':{
    id:'survival-endurance',label:'SURVIVAL',description:'Single-round roster endurance with stronger pressure and partial health recovery between rivals.',roundsToWin:1,timerSeconds:75,cpuProfile:'aggressive',autoResetOnKo:false,ladder:true,carryHealthBetweenOpponents:true,recoveryBetweenOpponents:.18,rotateArenaBetweenOpponents:true,
  },
}

export function fightExperienceRules(mode:FightExperienceMode):FightExperienceRules{return FIGHT_EXPERIENCE_RULES[mode]}

export function nextEnduranceHealthRatio(currentHealth:number,maxHealth:number,recovery:number){
  if(!Number.isFinite(currentHealth)||!Number.isFinite(maxHealth)||maxHealth<=0)throw new Error('Endurance health requires finite positive maxHealth')
  if(!Number.isFinite(recovery)||recovery<0||recovery>1)throw new Error('Endurance recovery must be between 0 and 1')
  const current=Math.max(0,Math.min(1,currentHealth/maxHealth))
  return Math.min(1,current+recovery)
}

export function cpuIntentForProfile(profile:CpuProfile,self:Combatant,target:Combatant):Intent{
  if(profile==='standard')return aiIntent(self,target)
  if(profile==='training-dummy'){
    const threat=target.attackPhase==='startup'||target.attackPhase==='active'
    return threat&&self.guardMeter>0?{...EMPTY_INTENT,block:true}:EMPTY_INTENT
  }
  const base=aiIntent(self,target)
  const distance=Math.abs(self.x-target.x)
  if(self.superMeter>=100)return{...EMPTY_INTENT,special:true}
  if(distance<=2.5&&self.stamina>=STRIKES.heavy.stamina&&!target.downed)return{...EMPTY_INTENT,heavy:true}
  if(distance>3.8&&self.stamina>32)return{...base,dash:true}
  return base
}
